import React, { useEffect, useState } from "react";
import {
  getPushSubscriptionStatus,
  getNotificationPermission,
  subscribeToAllOrganizations,
} from "./pushNotifications";

const FONT = "'Plus Jakarta Sans', system-ui, -apple-system, sans-serif";

const NotificationWarning = ({ message, onTurnOn }) => {
  const [enabled, setEnabled] = useState(true);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  // Same flow as NotificationAccess: one browser prompt, then subscribe this
  // device for the current org, then tell the rest of the app.
  const turnOn = async () => {
    if (onTurnOn) return onTurnOn();
    const orgId = JSON.parse(localStorage.getItem("org") || "null");
    if (!orgId || busy) return;
    setBusy(true);
    try {
      const result = await subscribeToAllOrganizations([orgId]);
      if (result?.granted && result?.orgIds?.length > 0) {
        window.dispatchEvent(
          new CustomEvent("notification-status-changed", { detail: { enabled: true } }),
        );
      } else if (getNotificationPermission() === "denied") {
        setBlocked(true);
      }
    } catch (e) {
      console.error("Failed to enable notifications:", e);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    const checkNotificationStatus = async () => {
      const status = await getPushSubscriptionStatus();
      setEnabled(status);
    };

    checkNotificationStatus();

    const handleNotificationStatusChange = (event) => {
      setEnabled(event.detail.enabled);
    };

    window.addEventListener(
      "notification-status-changed",
      handleNotificationStatusChange,
    );

    return () => {
      window.removeEventListener(
        "notification-status-changed",
        handleNotificationStatusChange,
      );
    };
  }, []);

  if (enabled) {
    return null;
  }

  // Same wording as before, laid out as the design's card:
  // bold "Notifications are off" title, the rest of the message as the body.
  const fullText =
    message ||
    "Notifications are off. Enable them from your profile menu to receive CRM alerts.";
  const TITLE = "Notifications are off";
  const body = fullText.startsWith(TITLE)
    ? fullText.slice(TITLE.length).replace(/^[.\s]+/, "")
    : fullText;

  return (
    <div
      role="status"
      style={{
        display: "flex",
        gap: 9,
        margin: "0 0 16px",
        padding: "12px 14px",
        background: "#FDF4E1",
        border: "1px solid #F3E0B4",
        borderRadius: 12,
        fontFamily: FONT,
        textAlign: "left",
      }}
    >
      <span
        style={{
          flex: "0 0 7px",
          width: 7,
          height: 7,
          marginTop: 6,
          borderRadius: "50%",
          background: "#E0A21B",
        }}
      />
      <div style={{ minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.35, color: "#6B4406" }}>
          {TITLE}
        </div>
        {body && (
          <div style={{ marginTop: 2, fontSize: 12, fontWeight: 500, lineHeight: 1.45, color: "#8A5A08" }}>
            {body}
          </div>
        )}
        {blocked ? (
          <div style={{ marginTop: 8, fontSize: 12, fontWeight: 600, lineHeight: 1.45, color: "#A6342A" }}>
            Blocked in this browser — allow notifications from the lock icon next to the address bar, then reload.
          </div>
        ) : getNotificationPermission() !== "unsupported" && (
          <button
            type="button"
            onClick={turnOn}
            disabled={busy}
            style={{
              marginTop: 8,
              padding: 0,
              background: "transparent",
              border: 0,
              cursor: "pointer",
              fontFamily: FONT,
              fontSize: 12.5,
              fontWeight: 700,
              color: "#6B4406",
            }}
          >
            {busy ? "Turning on…" : "Turn on"}
          </button>
        )}
      </div>
    </div>
  );
};

export default NotificationWarning;
